import {
    useCallback, useState, useEffect,
} from 'react'

import FormWithControls from 'app/shared-components/FormWithControls'
import useGetContractBasis from 'app/Apps/Pricing/ContractBasis/hooks/useGetContractBasis'
import Button from 'app/shared-components/Button'
import useSkyNetDomainContext from 'app/shared-components/SkyNetDomain/useSkyNetDomainContext'
import {
    QuoteStatus,
} from 'app/enums/label.enums'
import requests from 'app/requests/Quote/Quote.requests'
import QuoteInformationForm from '../QuoteInformationForm'
import {
    AQuote,
} from '../AQuotes.types'
import useAssignToContractBasis from './hooks/useAssignToContractBasis'

const AssignQuote = ({
    data,
    invalidate,
}: {
    data: AQuote,
    invalidate: () => void
}) => {
    const [
        value,
        setValue,
    ] = useState(data)

    const [
        defaultContractBasis,
        setDefaultContractBasis,
    ] = useState(value?.plannedContractBasis)

    const {
        data: contractBasis,
        isDataReady: isDataReadyContractBasis,
    } = useGetContractBasis(
        value?.quoteStatus === QuoteStatus.CREATED ? value?.plannedContractBasis?.id : null,
    )

    useEffect(() => {
        if (
            contractBasis && value?.plannedContractBasis?.id !== defaultContractBasis?.id
            && contractBasis?.id === value?.plannedContractBasis?.id
        ) {
            setDefaultContractBasis(value?.plannedContractBasis)
            setValue({
                ...value,
                currency: contractBasis?.currency,
            })
        }
    }, [
        contractBasis,
        defaultContractBasis?.id,
        value,
        isDataReadyContractBasis,
    ])

    const {
        updateTableElement,
    } = useSkyNetDomainContext()

    const onSuccess = useCallback(() => {
        invalidate()
        updateTableElement({
            ...value,
            pricingResponsible: [],
        })
    }, [
        invalidate,
        updateTableElement,
        value,
    ])

    const assignToContractBasis = useAssignToContractBasis({
        id: data.id,
        onSuccess,
    })

    const getCustomButtons = useCallback(() => {
        const customButton = (
            <Button
                key="assing-to-contract-basis"
                label={data?.quoteStatus === QuoteStatus.CREATED ? 'Assign To Contract Basis' : 'Reassign To Contract Basis'}
                onClick={assignToContractBasis}
                disabled={data?.linkedContractBasis?.id
                    === data?.plannedContractBasis?.id}
            />
        )

        return (data?.quoteStatus === QuoteStatus.CREATED
            || data?.quoteStatus === QuoteStatus.IN_PROGRESS)
        && data.plannedContractBasis?.id ? [customButton] : []
    }, [
        data?.quoteStatus,
        data?.linkedContractBasis,
        data?.plannedContractBasis,
        assignToContractBasis,
    ])

    return (
        <QuoteInformationForm
            value={value}
            setValue={setValue}
            showContractBasisLink
            hasPlannedContractBasis={Boolean(data?.plannedContractBasis?.id)}
            render={({
                fields, formData, onChange,
            }) => {
                return (
                    <FormWithControls
                        setExternalValue={onChange}
                        value={formData}
                        name="AQuote"
                        requestParams={requests.update({
                            id: data.id,
                        })}
                        fields={fields}
                        onSuccess={invalidate}
                        onCancel={invalidate}
                        exists
                        getCustomButtons={getCustomButtons}
                    />
                )
            }}
        />
    )
}

export default AssignQuote
